import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PlatformService, SelectedCustomerState } from '@panamax/app-state';
import { ImageVariantEnum } from '@product-detail/models/image-variant.enum';
import { Product } from '@shared/models/product.model';
import { Note } from '@usf/alert-types';
import { MERCH_FEATURE, ProductTrackingInfo } from '@usf/ngrx-product';
import { BehaviorSubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { PdpLnkSrc } from 'src/app/shared/constants/pdp-lnksrc.enum';
import { ProductDetailViewModel } from '../../../product-detail/models/product-detail-view-model';
import { ProductDetailService } from '../../../product-detail/services/product-detail.service';
import {
  getAlternativeThumbnail,
  getFirstImageURL,
} from '../../../product-detail/utils/product-images.util';
import { NavigationHelperService } from '../../../shared/helpers/navigation.helpers.service';
import { QuantityUpdate } from '../../../shared/models/quantity-update';
import { ProductService } from '../../../shared/services/product/product.service';
import { SLIDER_OPTIONS } from './../../../product-detail/constants/constants';
import Swiper from 'swiper';

export interface ProductThumbnail {
  imageUrl: string;
  productNumber: number;
}

@Component({
  selector: 'app-tm-note-card',
  templateUrl: './tm-note-card.component.html',
  styleUrls: ['./tm-note-card.component.scss'],
})
export class TmNoteCardComponent implements OnInit, OnDestroy {
  readonly SLIDER_OPTIONS = SLIDER_OPTIONS;

  @Input() product: ProductDetailViewModel;
  @Input() tmNote: Note;
  lnkSrc = PdpLnkSrc.tmNotes;

  productThumbnails: ProductThumbnail[] = [];
  tmNoteTitleLimit = 40;
  tmNoteContentLimit = 300;

  productSwiperRef$: BehaviorSubject<Swiper> = new BehaviorSubject(null);

  selectedCustomer: SelectedCustomerState;
  associatedProducts: Product[];
  associatedProductsSub$: Subscription;
  getProductSub$: Subscription;
  startSlice = 0;
  endSlice = 12;

  @ViewChild('productSwiperRef') productSwiperRef: any;

  sliderValidationOptions = {
    ASSOCIATED_PRODUCTS: {
      isFirstSlide: true,
      isFinalSlide: false,
    },
  };

  constructor(
    readonly platformService: PlatformService,
    private productService: ProductService,
    private productDetailService: ProductDetailService,
    private readonly navHelper: NavigationHelperService,
  ) {}

  // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product?.currentValue) {
      this.product = changes.product?.currentValue;
      this.loadCustomerRecommendations();
      this.checkProductPosition();
    }
  }

  ngOnInit(): void {
    if (this.tmNote) {
      if (this.tmNote.title.length > this.tmNoteTitleLimit) {
        this.tmNote.title =
          this.tmNote.title.slice(0, this.tmNoteTitleLimit) + '...';
      }
      if (this.tmNote.text.length > this.tmNoteContentLimit) {
        this.tmNote.text =
          this.tmNote.text.slice(0, this.tmNoteContentLimit) + '...';
      }
    }
    this.loadCustomerRecommendations();
  }

  ngOnDestroy() {
    if (this.associatedProductsSub$) {
      this.associatedProductsSub$.unsubscribe();
    }
    if (this.getProductSub$) {
      this.getProductSub$.unsubscribe();
    }
  }

  loadCustomerRecommendations = (): void => {
    const productTrackingInfos: ProductTrackingInfo[] = [];
    this.associatedProductsSub$ = this.productService
      .getTMNotesProducts(this.tmNote?.noteProdNbrs)
      .subscribe(products => {
        const productThumbnails: ProductThumbnail[] = [];
        this.associatedProducts = products;
        products.forEach(p => {
          productTrackingInfos.push({
            productNumber: p?.productNumber,
            attributes: p.trackingAttributes,
          });
          if (
            this.platformService?.platformType ===
            this.platformService?.platformEnum?.mobile
          ) {
            const currentProduct: ProductThumbnail = {
              imageUrl: getFirstImageURL(
                p?.summary,
                ImageVariantEnum.Thumbnail,
              ),
              productNumber: p?.productNumber,
            };
            productThumbnails.push(currentProduct);
          }
        });
        this.productThumbnails = productThumbnails;
        this.checkProductPosition();
      });
  };

  onQuantityUpdate(quantityUpdate: QuantityUpdate, rank: number) {
    this.getProductSub$ = this.productService
      .getProduct(quantityUpdate.productNumber)
      .pipe(take(1))
      .subscribe(product => {
        this.productDetailService.trackRelatedProductChangeQuantity(
          quantityUpdate,
          product,
          MERCH_FEATURE.tmNotesName,
          rank,
          this.tmNote.noteId,
        );
      });
  }

  nextProductsAssociated = (): void => {
    this.nextSlide(this.productSwiperRef);
  };

  prevProductsAssociated = (): void => {
    this.prevSlide(this.productSwiperRef);
  };

  nextSlide = (slider): void => {
    slider.slideNext();
  };
  prevSlide = (slider): void => {
    slider.slidePrev();
  };

  checkProductPosition = (): void => {
    this.checkSliderPosition(this.productSwiperRef);
  };

  checkSliderPosition(slider: Swiper) {
    let startPromise = slider?.isBeginning;
    let endPromise = slider?.isEnd;
    Promise.resolve(startPromise).then(isBeginning => {
      this.sliderValidationOptions.ASSOCIATED_PRODUCTS.isFirstSlide = isBeginning;
    });
    Promise.resolve(endPromise).then(isEnd => {
      this.sliderValidationOptions.ASSOCIATED_PRODUCTS.isFinalSlide = isEnd;
    });
  }

  goToDetails() {
    this.navHelper.routeToNoteDetails(this.tmNote.noteId);
  }

  getAlternateThumbnail = (thumbnailImageURL: string): string => {
    return getAlternativeThumbnail(this.product?.summary, thumbnailImageURL);
  };

  productClickHandler(productNumber: number) {
    this.navHelper.routeToProductDetails(productNumber);
  }

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }

  setSwiperInstance(swiper: Swiper) {
    this.productSwiperRef$.next(swiper);
  }
}
